import { useQuery } from "@tanstack/react-query";
import { parseAbiItem } from "viem";
import { useAccount } from "wagmi";

import type { VenueId } from "@/config/contracts";
import { VENUES } from "@/config/contracts";
import { SUPPLY_VAULT } from "@/config/vault";
import { buildPublicClient } from "@/lib/wallet/clients";

const LOOP_OPENED = parseAbiItem(
  "event LoopOpened(address indexed user, uint256 principal, uint32 leverageBps, uint8 borrowRounds, uint256 finalCollateral, uint256 totalBorrowed)",
);
const LOOP_UNWOUND = parseAbiItem(
  "event LoopUnwound(address indexed user, uint256 collateralReturned, uint256 usdcRepaid)",
);
const VAULT_DEPOSIT = parseAbiItem(
  "event Deposit(address indexed caller, address indexed owner, uint256 assets, uint256 shares)",
);
const VAULT_WITHDRAW = parseAbiItem(
  "event Withdraw(address indexed caller, address indexed receiver, address indexed owner, uint256 assets, uint256 shares)",
);

export type RecentTxn = {
  readonly kind: "open" | "close" | "supply" | "withdraw";
  readonly venue: VenueId | "vault";
  readonly cadc: bigint;
  readonly usdc: bigint;
  readonly blockNumber: bigint;
  readonly txHash: `0x${string}`;
};

export const useRecentTxns = () => {
  const { address } = useAccount();

  return useQuery({
    queryKey: ["recent-txns", address],
    enabled: Boolean(address),
    staleTime: 60_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<RecentTxn[]> => {
      const client = buildPublicClient();
      const latest = await client.getBlockNumber();
      // Earliest deployment across all our contracts — vault predates them.
      const floor = VENUES.ajna.deployedAt < VENUES.morpho.deployedAt
        ? VENUES.ajna.deployedAt
        : VENUES.morpho.deployedAt;

      const looperFetches = (["morpho", "ajna"] as const).flatMap((venue) => [
        client
          .getLogs({
            address: VENUES[venue].looper,
            event: LOOP_OPENED,
            args: { user: address },
            fromBlock: VENUES[venue].deployedAt,
            toBlock: latest,
          })
          .then((logs) =>
            logs.map((l) => ({
              kind: "open" as const,
              venue,
              cadc: l.args.finalCollateral!,
              usdc: l.args.totalBorrowed!,
              blockNumber: l.blockNumber!,
              txHash: l.transactionHash!,
            })),
          ),
        client
          .getLogs({
            address: VENUES[venue].looper,
            event: LOOP_UNWOUND,
            args: { user: address },
            fromBlock: VENUES[venue].deployedAt,
            toBlock: latest,
          })
          .then((logs) =>
            logs.map((l) => ({
              kind: "close" as const,
              venue,
              cadc: l.args.collateralReturned!,
              usdc: l.args.usdcRepaid!,
              blockNumber: l.blockNumber!,
              txHash: l.transactionHash!,
            })),
          ),
      ]);

      const vaultFetches = [
        client
          .getLogs({
            address: SUPPLY_VAULT.address,
            event: VAULT_DEPOSIT,
            args: { owner: address },
            fromBlock: floor,
            toBlock: latest,
          })
          .then((logs) =>
            logs.map((l) => ({
              kind: "supply" as const,
              venue: "vault" as const,
              cadc: 0n,
              usdc: l.args.assets!,
              blockNumber: l.blockNumber!,
              txHash: l.transactionHash!,
            })),
          ),
        client
          .getLogs({
            address: SUPPLY_VAULT.address,
            event: VAULT_WITHDRAW,
            args: { owner: address },
            fromBlock: floor,
            toBlock: latest,
          })
          .then((logs) =>
            logs.map((l) => ({
              kind: "withdraw" as const,
              venue: "vault" as const,
              cadc: 0n,
              usdc: l.args.assets!,
              blockNumber: l.blockNumber!,
              txHash: l.transactionHash!,
            })),
          ),
      ];

      const all = await Promise.all([...looperFetches, ...vaultFetches]);
      return all
        .flat()
        .sort((a, b) => Number(b.blockNumber - a.blockNumber))
        .slice(0, 10);
    },
  });
};
